import React, { useEffect, useState } from "react";
import API_URL from "../../services/api";
import "./Announcement.css";

const Announcement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // ============================
  // FETCH ANNOUNCEMENTS
  // ============================

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${API_URL}/api/announcements`
      );

      if (!res.ok) {
        throw new Error("Failed to fetch announcements");
      }

      const data = await res.json();

      const activeAnnouncements = data
        .filter((item) => item.status === "Active")
        .sort(
          (a, b) =>
            new Date(b.announcement_date) -
            new Date(a.announcement_date)
        );

      setAnnouncements(activeAnnouncements);
    } catch (err) {
      console.log("ANNOUNCEMENT ERROR:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // ============================
  // SEARCH
  // ============================

  const filteredAnnouncements =
    announcements.filter((item) => {
      const searchText = search.toLowerCase();

      return (
        (item.title || "")
          .toLowerCase()
          .includes(searchText) ||
        (item.description || "")
          .toLowerCase()
          .includes(searchText)
      );
    });

  // ============================
  // DATE FORMAT
  // ============================

  const formatDate = (date) => {
    if (!date) return "Date not available";

    return new Date(date).toLocaleDateString(
      "en-GB",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  return (
    <div className="user-announcement-page">

      {/* ============================
          HEADER
      ============================ */}

      <div className="announcement-header">

        <div className="announcement-header-icon">
          📢
        </div>

        <div>
          <h1>
            Library Announcements
          </h1>

          <p>
            Stay updated with the latest news,
            notices, and important library updates.
          </p>
        </div>

      </div>

      {/* ============================
          SEARCH
      ============================ */}

      <div className="announcement-search-wrapper">

        <input
          type="text"
          placeholder="Search announcements..."
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
          className="announcement-search"
        />

        {search && (
          <button
            className="clear-search"
            onClick={() => setSearch("")}
          >
            ✕
          </button>
        )}

      </div>

      {/* ============================
          RESULT COUNT
      ============================ */}

      {!loading &&
        announcements.length > 0 && (
          <div className="announcement-count">

            {search
              ? `${filteredAnnouncements.length} result${
                  filteredAnnouncements.length !== 1
                    ? "s"
                    : ""
                } found`
              : `${announcements.length} active announcement${
                  announcements.length !== 1
                    ? "s"
                    : ""
                }`}

          </div>
        )}

      {/* ============================
          LOADING
      ============================ */}

      {loading && (
        <div className="announcement-empty">

          <div className="loading-icon">
            ⏳
          </div>

          <h2>
            Loading Announcements
          </h2>

          <p>
            Please wait while we load the
            latest announcements.
          </p>

        </div>
      )}

      {/* ============================
          EMPTY
      ============================ */}

      {!loading &&
        filteredAnnouncements.length === 0 && (
          <div className="announcement-empty">

            <div className="empty-icon">
              {search ? "🔍" : "📢"}
            </div>

            <h2>
              {search
                ? "No Announcements Found"
                : "No Announcements Available"}
            </h2>

            <p>
              {search
                ? "Try searching with a different keyword."
                : "There are currently no active announcements."}
            </p>

            {search && (
              <button
                className="clear-button"
                onClick={() => setSearch("")}
              >
                Clear Search
              </button>
            )}

          </div>
        )}

      {/* ============================
          ANNOUNCEMENTS
      ============================ */}

      {!loading &&
        filteredAnnouncements.length > 0 && (
          <div className="announcement-grid">

            {filteredAnnouncements.map(
              (item) => (
                <article
                  key={item.id}
                  className="announcement-card"
                >

                  {/* Card Top */}

                  <div className="announcement-card-top">

                    <div className="announcement-date-box">

                      <span className="date-icon">
                        📅
                      </span>

                      <span>
                        {formatDate(
                          item.announcement_date
                        )}
                      </span>

                    </div>

                    <span className="active-badge">
                      Active
                    </span>

                  </div>

                  {/* Title */}

                  <h2 className="announcement-title">
                    {item.title}
                  </h2>

                  {/* Description */}

                  <p className="announcement-description">
                    {item.description}
                  </p>

                  {/* Footer */}

                  <div className="announcement-footer">

                    <span>
                      📢 Library Notice
                    </span>

                    <span>
                      {formatDate(
                        item.announcement_date
                      )}
                    </span>

                  </div>

                </article>
              )
            )}

          </div>
        )}

    </div>
  );
};

export default Announcement;